import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import ErrorWrapper from '@components/BaseComponents/ErrorWrapper';
import Form from "deltoid/core/Form";
import PayUIcons from "deltoid/core/PayUIcons";
import withSizes from "react-sizes";

import { Row, Col } from "deltoid/core/Blocks/BootstrapContainers";
import { H, Small } from 'deltoid/core/Blocks/Typography';
import FormFooter from '@components/BaseComponents/FormFooter';
import {
  panDetailsActions,
  panDetailsSelectors,
  merchantSelectors,
  masterDataSelectors,
  dialogActions,
} from "@store/ducks";
import { pancardNumberRegex } from "@utils/validations";
import Dialog from "./Dialog";

const PanDetails = ({
  verifyPan,
  verifying,
  businessEntities,
  panDetailsErrMsg,
  filled: filledProp,
  editable,
  closeDialog,
  isMobile,
  merchantDetails,
  why,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [filled, setFilled] = useState(filledProp);
  const { register, handleSubmit, errors, triggerValidation } = useForm();

  useEffect(() => {
    setErrMsg(panDetailsErrMsg);
    if (panDetailsErrMsg) {
      setTimeout(() => triggerValidation("panCardNumber"), 200);
    }
  }, [panDetailsErrMsg]);

  const onSubmit = (formData) => {
    verifyPan(formData.panCardNumber);
  };

  const { pan_business_entity_mapping, list } = businessEntities || {};

  const acceptedEntities = (value) => {
    if (!value || (value && value.length < 5)) return;

    const valid4Char = Object.keys(pan_business_entity_mapping);
    if (!valid4Char.includes(value[3].toUpperCase()) && valid4Char.length) {
      return "This is no a recognised Entity type in our system, please try with a different PAN number";
    }
    return;
  };

  const entityType = list ? (
    list.find(entity => entity.id === (merchantDetails && merchantDetails.business_entity_id))
  ) : 'NA';

  return (
    <>
      {filled ? (
        <Row className='p-4'>
          <Col>
            <H level={5} className="pb-3">
              {merchantDetails ? merchantDetails.pancard_number : 'NA'}
            </H>
            <Small>{entityType ? entityType.name : 'NA'}</Small>
            {editable && (
              <Form.Button
                onClick={() => setFilled(false)}
                variant="underlined"
              >
                Edit PAN
              </Form.Button>
            )}
          </Col>
        </Row>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className='p-4'>
            <Col md={6}>
              <Form.Group className="position-relative">
                <div
                  className={`pan-info-tooltip${isMobile ? " mobile" : ""} ${
                    showTooltip ? " show" : ""
                  }`}
                >
                  <div className="d-flex flex-row align-items-baseline">
                    <PayUIcons type="payout" />
                    <p className="pl-1 m-0">
                      Enter your personal PAN number if you are an individual or
                      a Sole Proprietor. For other entities enter your business
                      PAN details
                    </p>
                  </div>
                </div>
                <Form.Label id="panCardNumber" htmlFor="panCardNumber">
                  Business PAN Card number
                </Form.Label>
                <Form.Text
                  className='text-uppercase'
                  id="panCardNumber"
                  name="panCardNumber"
                  type="text"
                  ref={register({
                    required: "Pan Card number can not be empty",
                    validate: {
                      regex: pancardNumberRegex,
                      acceptedEntities,
                      doesNotExist: (panNumber) =>
                        panNumber && !errMsg ? null : errMsg,
                    },
                  })}
                  onChange={() => setErrMsg(null)}
                  autoComplete="off"
                  onFocus={() => setShowTooltip(true)}
                  onBlur={() => setShowTooltip(false)}
                />
                <ErrorWrapper errors={errors} fieldName='panCardNumber' data-testid='pan-validation-error'/>
              </Form.Group>
            </Col>
          </Row>
          <FormFooter
            tooltipProps={why}
            btnProps={{
              label: verifying ? "Verifying... " : "Proceed to Verify",
              type: "submit",
              disabled: verifying ? true : false,
              loading: verifying
            }}
          />
          <Dialog onClose={closeDialog} />
        </form>
      )}
    </>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 768,
});

const Responsive = withSizes(mapSizesToProps)(PanDetails);

const mapStateToProps = (state) => {
  return {
    verifying: panDetailsSelectors.verifying(state),
    panDetailsErrMsg: panDetailsSelectors.errMsg(state),
    businessEntities: masterDataSelectors.businessEntities(state),
    merchantDetails: merchantSelectors.getMerchantDetails(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  verifyPan: (panCardNumber) =>
    dispatch(panDetailsActions.verifyPan(panCardNumber)),
  closeDialog: () => dispatch(dialogActions.close()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Responsive);
